import Handlebars from '@sparta.global.vendors/handlebars';
import { badgingHelper } from '@sparta.global.helpers/badging-helper/dist/es5.js';

/* eslint-disable max-len */

/**
 * Constant for storing message strings.
 */
var STRINGS = {
  NO_TAB_NAV_JCR_ERROR: 'No tab JCR objects provided to helper-tab.',
  NO_TAB_NAV_CONTENT_ERROR: '<strong>Error:</strong> No tab content available.',
  NO_TAB_NAV_LEVEL3_ERROR: 'You have used a panel object with a L3 tab set. This is discouraged and may cause unintended issues.'
};

/**
 * Gets Handlebars-usable template string.
 * @param {string} partials - Object of available partials to use from JCR.
 * @param {string} partialName - Name of Handlebars partial file.
 * @param {string} panelContent - Standard string which may or may not contain HTML.
 * @param {string} [dynamicContent] - What type of content is being requested ("partial",
 *   "markdown", or null]).
 * @returns {function} Compiled Handlebars template.
 */
function getContentChunk(partials, partialName) {
  return partials[partialName] ? partials[partialName] : Handlebars.compile('{{{content.markdown}}}');

  /*
  const setTemplate = () => {
    const type = {
      partial: `{{> ${partialName} }}`,
      markdown: '{{{content.markdown}}}',
      default: panelContent,
    };
    return (type[dynamicContent] || type.default);
  };
  const templateString = setTemplate();
  return Handlebars.compile(templateString);
  */
}

/**
 * Loops through the JCR contexts and matches the context for the current panel content, then
 * compiles and returns the content into a Handlebars template string.
 * @param {object} partials - Object of partials provided by module.
 * @param {object} panel - Object of JCR parameters for the current panel.
 * @param {string} partialName - Name of Handlebars partial file.
 * @param {string} panelContent - Standard string which may or may not contain HTML.
 * @param {string} [dynamicContent] - What type of content is being requested.
 * @param {object} contexts - JCR contexts provided by the Handlebars template.
 * @param {function} dataKey - The key for `contexts` to specify the panel context.
 * @returns {string} Compiled Handlebars template string with dynamic panel content.
 */
function getDynamicContent(partials, panel, partialName, panelContent, dynamicContent, contexts, dataKey) {
  var template = getContentChunk(partials, partialName);
  if (!contexts) {
    return false;
  }

  // Match content to JCR.
  var panelContext;
  contexts.forEach(function (context) {
    if (panel.content === context.jcr && !dataKey) {
      panelContext = context;
    }
  });
  if (!panelContext) {
    if (contexts.length) {
      panelContext = dataKey ? contexts[dataKey] : contexts[0];
    } else {
      panelContext = contexts;
    }
  }

  // Check to make sure we have a file string to look up.
  var newPanelContent;
  if (contexts && contexts.length) {
    // If content comes from a partial...
    if (dynamicContent === 'partial') {
      newPanelContent = template(panelContext);
    } else if (dynamicContent === 'markdown') {
      // If content comes from Markdown...
      newPanelContent = template({
        content: panelContext
      });
    } else {
      // Otherwise, just use the string as given for content.
      newPanelContent = panel.content;
    }
  } else if (panel.content) {
    newPanelContent = panel.content;
  } else {
    // If no context given, print error.
    newPanelContent = STRINGS.NO_TAB_NAV_CONTENT_ERROR;
  }

  // Return Handlebars template.
  return newPanelContent;
}

/**
 * Returns child module content into the DOM
 * @param {object} jcrContexts - Combine all the additional arguments into an array
 * @param {object} panel - element object for the targeted DOM element
 * @returns {string} Compiled Handlebars template string with dynamic panel content.
 */
function getChildModule(jcrContexts, panel) {
  var innerContent;

  // If the content is a child module...
  if (jcrContexts.length && panel.moduleId) {
    var moduleContent = null;
    jcrContexts.every(function (context) {
      if (Object.prototype.hasOwnProperty.call(context, panel.moduleId)) {
        moduleContent = context[panel.moduleId];
        return false;
      }
      return true;
    });
    if (moduleContent !== null) {
      innerContent = "\n        <div class=\"content\">\n          <div class=\"row collapse\">\n            <div class=\"column\">".concat(moduleContent, "</div>\n          </div>\n        </div>\n      ");
    } else {
      innerContent = "\n        <div class=\"content\">\n          <div class=\"row\">\n            <div class=\"column\">".concat(STRINGS.NO_TAB_CONTENT_ERROR, "</div>\n          </div>\n        </div>\n      ");
    }
  } else if (panel.moduleName) {
    innerContent = "\n      <div class=\"content\">\n        <div class=\"row collapse\">\n          <div class=\"column\">\n            <div data-panel-content=\"".concat(panel.moduleName, "\"></div>\n          </div>\n        </div>\n      </div>\n    ");
  } else {
    innerContent = "\n      <div class=\"content\">\n        <div class=\"row\">\n          <div class=\"column\">".concat(STRINGS.NO_TAB_CONTENT_ERROR, "</div>\n        </div>\n      </div>\n    ");
  }

  // return string content
  return innerContent;
}

/**
 * Create controls for sliding.
 * @param {settings} settings - Object that contains class names, elements, and helper options.
 * @param {object} type - Control for next/prev settings.
 * @returns {string} - Returns control string with next/prev settings.
 */
function createControl(settings, type) {
  var ariaLabel = '';
  var direction = type === 'prev' ? 'left' : 'right';

  // Previous ADA label.
  if (settings.controlPrevADA && type === 'prev') {
    ariaLabel = "aria-label=\"".concat(settings.controlPrevADA, "\"");
  } else if (type === 'prev') {
    ariaLabel = 'aria-label="Slide left"';
  }

  // Next ADA label.
  if (settings.controlNextADA && type === 'next') {
    ariaLabel = "aria-label=\"".concat(settings.controlNextADA, "\"");
  } else if (type === 'next') {
    ariaLabel = 'aria-label="Slide right"';
  }

  // Create control string.
  return "\n    <button\n      class=\"spa-tab__control spa-tab__control--hidden spa-tab__control--".concat(type, "\"\n      type=\"button\"\n      tabindex=\"-1\"\n      aria-hidden=\"true\"\n      data-tab-control-speed=\"").concat(settings.controlSpeed, "\"\n      data-tab-control-increment=\"").concat(settings.controlIncrement, "\"\n      ").concat(ariaLabel, "\n    >\n      <span class=\"spa-tab__icon spa-tab__icon-control\">\n        <span class=\"spa-tab__icon-wrapper\">\n          <span class=\"spa-icon icon-caret-").concat(direction, "--gray spa-tab__icon--default\" aria-hidden=\"true\"></span>\n          <span class=\"spa-icon icon-caret-").concat(direction, "--gray-20 spa-tab__icon--disabled\" aria-hidden=\"true\"></span>\n          <span class=\"spa-icon icon-caret-").concat(direction, "--light-blue spa-tab__icon--hover\" aria-hidden=\"true\"></span>\n          <span class=\"spa-icon icon-caret-").concat(direction, "--dark-blue spa-tab__icon--focus\" aria-hidden=\"true\"></span>\n        </span>\n      </span>\n    </button>\n  ");
}

/**
* Pieces together all the HTML elements needed to make tab navigation.
* @param {object} partials - Object of partials provided by module JCR.
* @param {object} jcr - JSON object used to build the tabHelper.
* @returns {Node} `Handlebars.SafeString` version of `spa-tab__container` element.
*/
function tabNavigation(partials, jcr) {
  if (!jcr) {
    throw new Error(STRINGS.NO_TAB_NAV_JCR_ERROR);
  }

  // Get default options.
  var options = jcr;

  // Combine all the additional arguments into an array of contexts for the partials/Markdown
  // If 'navigation' type we combine all and slice first instance since that's jcr data.
  // If 'standard' type than we need to gather the args since its combined in that helpers call.
  // Dev note: If not set, that means 'navigation' tab-nav helper. Else we're assuming 'standard'
  var jcrContexts;
  for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }
  if (typeof options.type === 'undefined') {
    jcrContexts = options.isVue ? args : Array.prototype.slice.call(args, 0, -1);
  } else {
    jcrContexts = args;
    if (jcrContexts.length > 0 && Array.isArray(jcrContexts[0])) {
      jcrContexts = jcrContexts[0];
    }
  }

  // Create settings object from JCR information.
  var settings = new function SettingsContructor() {
    // Create randomized key for when some options are not given by JCR.
    this.randomized = "".concat(Math.floor(Math.random() * 90000) + 10000);

    // Create unique ID for this tab set.
    this.id = options.id || "tabNavHelper-".concat(this.randomized);

    // JCR options that enable actions when utility JS is fired.
    this.debug = options.debug;

    // Type controls
    this.type = options.type || 'navigation';
    this.level = this.type === 'navigation' ? 'one' : options.level || 'two';

    // JCR options that enable actions within the helper.
    this.class = 'class' in options ? options.class : '';
    this.spinner = options.spinner === false ? 'data-spinner="false"' : 'data-spinner="true"';
    this.spinnerText = options.spinnerText ? "data-spinner-text=\"".concat(options.spinnerText, "\"") : '';
    this.fullWidthLayout = options.fullWidthLayout === true;
    this.activateTabByUrl = options.activateTabByUrl === true;
    this.addPanelBorder = 'addPanelBorder' in options ? options.addPanelBorder : false;
    this.dismissibleCloseADA = options.dismissibleCloseADA || 'close';
    this.dismissibleCloseDivider = options.dismissibleCloseDivider || ': ';
    this.dismissibleADA = options.dismissibleADA ? "".concat(this.dismissibleCloseADA).concat(this.dismissibleCloseDivider).concat(options.dismissibleADA) : "".concat(this.dismissibleCloseADA).concat(this.dismissibleCloseDivider);
    this.dismissible = this.type === 'navigation' && options.dismissible === true;

    // Determine if panels should have a max-height
    this.maxHeight = 'maxHeight' in options ? "".concat(options.maxHeight) : null;
    this.maxHeight = this.maxHeight && !new RegExp(/\d+(\.\d+)?(px|%|em|rem)/g).test(this.maxHeight) ? "".concat(this.maxHeight, "px") : this.maxHeight;
    this.ariaLabel = options.ariaLabel ? "aria-label=\"".concat(options.ariaLabel, "\"") : '';

    // Core tabs.
    this.tabs = options.tabs;

    // Tab controls
    this.controls = options.controls;
    this.controlSpeed = this.controls && this.controls.speed ? this.controls.speed : 20;
    this.controlIncrement = this.controls && this.controls.stepIncrement ? this.controls.stepIncrement : 25;
    this.controlPrevADA = this.controls && this.controls.prev && this.controls.prev.ariaLabel ? this.controls.prev.ariaLabel : 'Slide left';
    this.controlNextADA = this.controls && this.controls.next && this.controls.next.ariaLabel ? this.controls.next.ariaLabel : 'Slide right';
  }();

  // Individual tabs and panels.
  var tabKeys = Object.keys(settings.tabs);
  var tabs = '';
  var panels = '';
  var isLevelThreeWithPanel = false;
  var _loop = function _loop(i) {
    // Tab settings.
    var item = settings.tabs[tabKeys[i]];
    var tabSettings = new function ClassNamesContructor() {
      // Create randomized key for when some options are not given by JCR.
      this.randomized = "".concat(Math.floor(Math.random() * 90000) + 10000);

      // Base settings for each tab
      this.tabID = item.id ? "tab-".concat(item.id) : "tab-".concat(this.randomized);
      this.panelID = item.id ? "panel-".concat(item.id) : "panel-".concat(this.randomized);
      this.label = item.label || '';
      this.panel = item.panel;
      this.options = item.options;
      this.tabClass = 'class' in item ? item.class : '';
      this.selected = item.selected;

      // Enable tab activation by URL.
      this.activateTabByUrl = item.activateTabByUrl || settings.activateTabByUrl ? 'spa-tab__activate-by-url' : '';

      // Enable tab activation by JCR value.
      this.activateByJCR = this.selected ? 'spa-tab__activate-on-load' : '';

      // DOM element that is used for tab
      this.type = item.type === 'a' || item.type === 'link' ? 'a' : 'button';
      this.isButton = this.type === 'button';
      this.role = item.role === 'button' || item.role === 'link';

      // If DOM element is an anchor <a> than check for URL and TARGET attributes.
      this.elementTarget = !this.isButton && item.TARGET ? "target=\"".concat(item.TARGET, "\"") : '';
      this.elementURL = !this.isButton && item.URL ? "href=\"".concat(item.URL, "\"") : '';

      // Dynamic data binding
      this.tabPreserve = 'preserveTabContent' in item ? "data-preserve=\"".concat(item.preserveTabContent, "\"") : '';

      // See if this tab is open by default.
      this.shouldExpand = this.isButton && this.selected;
      this.hasTarget = item.TARGET ? true : false;
      this.isTabNavButtonLinkHash = (settings.type === 'navigation' || settings.type === 'standard') && (this.isButton || this.type === 'a' && !this.hasTarget);
      // Set aria label attribute.
      this.ariaLabel = !(settings.type === 'standard' && this.type === 'button') && this.isTabNavButtonLinkHash && item.ariaLabel ? "aria-label=\"".concat(item.ariaLabel, "\"") : '';

      // Update tab index and length markers with numeric values.
      this.ariaLabel = this.ariaLabel.replace('[tabLabel]', this.label);
      this.ariaLabel = this.ariaLabel.replace('[tabIndex]', i + 1);
      this.ariaLabel = this.ariaLabel.replace('[tabLength]', tabKeys.length);

      // Adds popup attribute based on type.
      this.ariaHasPopup = this.isButton && settings.type === 'navigation' ? 'aria-haspopup="true"' : '';

      // Is panel expanded on load.
      this.ariaExpanded = this.shouldExpand ? 'aria-expanded="true"' : 'aria-expanded="false"';
      this.ariaExpanded = settings.type === 'standard' ? '' : this.ariaExpanded;
      this.ariaExpanded = this.isButton ? this.ariaExpanded : '';

      // Is panel open on load.
      this.ariaSelected = this.shouldExpand ? 'aria-selected="true"' : 'aria-selected="false"';
      this.ariaSelected = settings.type === 'standard' && this.isButton ? this.ariaSelected : '';

      // Attach relationship between tab and panel.
      this.ariaControls = this.isButton && settings.type === 'standard' ? "aria-controls=\"".concat(this.panelID, "\"") : '';

      // Label panel by tab name.
      this.ariaLabelledBy = this.isButton && settings.type === 'standard' ? "aria-labelledby=\"".concat(this.tabID, "\"") : '';

      // tab icon settings.
      this.icon = item.icon;
      this.iconName = this.icon ? this.icon.name : null;

      // Default panel settings to globals if they don't exist
      if (this.isButton && this.panel) {
        this.panel.dismissible = 'dismissible' in this.panel ? this.panel.dismissible : settings.dismissible;
        if ('dismissibleADA' in this.panel) {
          // Remove close default if it's been added.
          this.panel.dismissibleADA = this.panel.dismissibleADA.replace("".concat(settings.dismissibleADA), '');

          // Add close with custom label.
          this.panel.dismissibleADA = "".concat(settings.dismissibleADA).concat(this.panel.dismissibleADA);
        } else {
          // Add close with default label.
          this.panel.dismissibleADA = "".concat(settings.dismissibleADA).concat(this.label);
        }
      }

      // Add badging
      this.badging = '';
      if (item.badging) {
        item.badging.options = item.badging.options || {};
        item.badging.options.position = 'relative';
        this.badging = badgingHelper(item.badging);
      }
    }();

    // Get tab data options.
    var tabOptions = tabSettings.options ? "data-options='".concat(JSON.stringify(new Handlebars.SafeString(tabSettings.options).string), "'") : '';

    // Single out panel so we can parse the object faster.
    var panel = tabSettings.panel || null;
    if (tabSettings.isButton && !panel) {
      panel = {};
    } else if (settings.level === 3 && tabSettings.panel) {
      isLevelThreeWithPanel = true;
    }

    // Panel content creation
    var innerHeadContent = '';
    var innerContent = '';
    var innerFootContent = '';

    // Get inner header content.
    if (panel && panel.contentHead) {
      innerHeadContent = getDynamicContent(partials, panel, panel.partialHeadName, panel.contentHead, panel.dynamicHeadContent, jcrContexts, panel.contentHeadKey);
    }

    // Get inner body content.
    if (panel && panel.content) {
      innerContent = getDynamicContent(partials, panel, panel.partialName, panel.content, panel.dynamicContent, jcrContexts, panel.contentKey);
    } else if (panel && panel.dynamicContent === 'module') {
      // if content is a child module
      innerContent = getChildModule(jcrContexts, panel);
    }

    // Get inner footer content.
    if (panel && panel.contentFoot) {
      innerFootContent = getDynamicContent(partials, panel, panel.partialFootName, panel.contentFoot, panel.dynamicFootContent, jcrContexts, panel.contentFootKey);
    }

    // Add icon support to tabs.
    if (tabSettings.iconName) {
      tabSettings.label = "\n        <span class=\"spa-tab__icon\">\n          <span class=\"spa-tab__icon-wrapper\">\n            <span class=\"spa-icon icon-".concat(tabSettings.iconName, "--gray spa-tab__icon--default\" aria-hidden=\"true\"></span>\n            <span class=\"spa-icon icon-").concat(tabSettings.iconName, "--light-blue spa-tab__icon--hover\" aria-hidden=\"true\"></span>\n            <span class=\"spa-icon icon-").concat(tabSettings.iconName, "--dark-blue spa-tab__icon--focus\" aria-hidden=\"true\"></span>\n            <span class=\"spa-icon icon-").concat(tabSettings.iconName, "--gray-20 spa-tab__icon--disabled\" aria-hidden=\"true\"></span>\n          </span>\n          <span class=\"spa-tab__icon-text\">\n            ").concat(tabSettings.label, "\n          </span>\n        </span>\n      ");
    }

    // Write tab HTML.
    var tabHTML = "\n      <".concat(tabSettings.type, "\n        id=\"").concat(tabSettings.tabID, "\"\n        class=\"spa-tab__tab-element ").concat(tabSettings.tabClass, " ").concat(tabSettings.activateTabByUrl, " ").concat(tabSettings.activateByJCR, "\"\n        ").concat(tabSettings.elementURL, "\n        ").concat(tabSettings.elementTarget, "\n        ").concat(tabSettings.type === 'button' ? 'type="button"' : '', "\n        ").concat(tabSettings.tabPreserve, "\n        ").concat(tabSettings.ariaControls, "\n        ").concat(tabSettings.ariaExpanded, "\n        ").concat(tabSettings.ariaSelected, "\n        ").concat(tabSettings.ariaLabel, "\n        ").concat(tabOptions, "\n        ").concat(settings.type === 'standard' && tabSettings.type === 'button' ? 'role="tab"' : '', "\n        ").concat(settings.type === 'standard' ? 'tabindex="-1"' : '', "\n      >\n        ").concat(tabSettings.label, "\n        ").concat(tabSettings.type === 'button' && settings.type === 'navigation' ? "\n            <span class=\"spa-tab__icon spa-tab__icon--small\">\n              <span class=\"spa-tab__icon-wrapper\">\n                <span class=\"spa-icon icon-chevron-down--light-blue spa-tab__icon--hover\" aria-hidden=\"true\"></span>\n              </span>\n            </span>\n          " : '', "\n        ").concat(tabSettings.badging, " \n      </").concat(tabSettings.type, ">\n    ");
    var panelHTML = panel ? "\n      <div\n        id=\"".concat(tabSettings.panelID, "\"\n        class=\"\n          spa-tab__list-item-dropdown\n          spa-tab__list-item-dropdown--").concat(settings.type, "\n          ").concat(settings.fullScreen ? 'spa-tab__list-item-fullscreen' : '', "\n          ").concat(settings.type === 'standard' && !settings.addPanelBorder ? 'spa-tab__layout-flush' : '', "\n          spa-card\n          spa-card--medium\n          spa-card--overlay\n          ").concat(panel.flyoutWidth === 'auto' ? 'spa-tab__auto--width' : panel.flyoutWidth ? 'spa-tab__custom--width' : '', "\n        \"\n        ").concat(tabSettings.ariaLabelledBy, "\n        ").concat(tabSettings.tabPreserve, "\n        ").concat(settings.spinner, "\n        ").concat(settings.spinnerText, "\n        ").concat(settings.maxHeight ? "data-max-height= \"".concat(settings.maxHeight, ";\"") : '', "\n        ").concat(settings.type === 'standard' ? 'role="tabpanel" tabindex="0"' : '', "\n        ").concat(panel.flyoutWidth ? "data-max-width= \"".concat(panel.flyoutWidth, "\"") : '', "\n      >\n        ").concat(settings.fullWidthLayout ? '<div class="row">' : '', "\n          ").concat(settings.fullWidthLayout ? '<div class="column">' : '', "\n            <div class=\"spa-tab-card-wrapper\">\n              <div class=\"spa-card__head ").concat(!panel.dismissible ? 'spa-card__hide-dismissible' : '', "\">\n                ").concat(panel.panelTitle || panel.dismissible ? "<div class=\"spa-card__head-wrapper\">\n                      ".concat(panel.panelTitle ? "\n                        <div class=\"spa-card__head-content-wrapper\">\n                          <".concat(panel.panelTitleTag ? panel.panelTitleTag : 'h3', " class=\"spa-card__title\">\n                            ").concat(panel.panelTitle, "\n                          </").concat(panel.panelTitleTag ? panel.panelTitleTag : 'h3', ">\n                          <div class=\"spa-card__head-content\">\n                            ").concat(innerHeadContent, "\n                          </div>\n                        </div>\n                      ") : "\n                        <div class=\"spa-card__head-content\">".concat(innerHeadContent, "</div>\n                      "), "\n                      ").concat(panel.dismissible ? "\n                        <button\n                          type=\"button\"\n                          class=\"spa-card__dismissible spa-icon icon-close\"\n                          ".concat(panel.dismissibleADA ? "aria-label=\"".concat(panel.dismissibleADA, "\"") : '', "\n                        ></button>\n                      ") : '', "\n                    </div>") : "\n                    <div class=\"spa-card__head-content\">\n                      ".concat(innerHeadContent, "\n                    </div>\n                  "), "\n              </div>\n \n              <div class=\"spa-card__loader\"></div>\n \n              <div class=\"spa-card__body\">\n                ").concat(innerContent, "\n              </div>\n \n              <div class=\"spa-card__foot spa-card__foot-content\">\n                ").concat(innerFootContent, "\n              </div>\n            </div>\n          ").concat(settings.fullWidthLayout ? '</div>' : '', "\n        ").concat(settings.fullWidthLayout ? '</div>' : '', "\n      </div>\n    ") : '';

    // Add tab to string output.
    // If navigation type, add list-item wrapper.
    tabs += settings.type === 'navigation' ? "<li class=\"spa-tab__tab-list-item spa-tab__panel-wrapper\">".concat(tabHTML).concat(panelHTML, "</li>") : "".concat(tabHTML);

    // Standard tabs have panel content after default list so glob it together
    panels += settings.type === 'standard' ? panelHTML : '';
  };
  for (var i = 0; i < tabKeys.length; ++i) {
    _loop(i);
  }

  // If level 3 tabs but with panel information, throw a warning
  if (isLevelThreeWithPanel === true) {
    // eslint-disable-next-line no-console
    console.warn('\x1b[33m%s\x1b[0m', STRINGS.NO_TAB_NAV_LEVEL3_ERROR);
  }

  // Compile everything together.
  var tabWrapper = "\n    <div\n      id=\"".concat(settings.id, "\"\n      class=\"\n        spa-tab__container\n        spa-tab--").concat(settings.type, "\n        spa-tab-level--").concat(settings.level, "\n        ").concat(settings.class, "\n        ").concat(settings.fullWidthLayout ? 'spa-tab--full-width' : '', "\n      \"\n      ").concat(settings.debug ? 'data-debug="true"' : '', "\n      ").concat(settings.type === 'navigation' ? 'data-close-on-click="true"' : '', "\n      >\n      ").concat(settings.fullWidthLayout ? '<div class="spa-tab__row row">' : '', "\n        ").concat(settings.fullWidthLayout ? '<div class="spa-tab__column">' : '', "\n          ").concat(settings.type === 'navigation'
  // Navigation tabs.
  ? "\n              <nav class=\"spa-tab__control-wrapper\" \n                ".concat(settings.ariaLabel, "\n              >\n                ").concat(createControl(settings, 'prev'), "\n                <ul class=\"spa-tab__tab-wrapper\">").concat(tabs, "</ul>\n                ").concat(createControl(settings, 'next'), "\n              </nav>\n              ") // Standard tabs.
  : "\n              <div class=\"spa-tab__control-wrapper\">\n                ".concat(createControl(settings, 'prev'), "\n                <div class=\"spa-tab__tab-wrapper\" role=\"tablist\">\n                  ").concat(tabs, "\n                </div>\n                ").concat(createControl(settings, 'next'), "\n              </div>\n              <div class=\"spa-tab__panel-wrapper\">").concat(panels, "</div>\n              "), "\n        ").concat(settings.fullWidthLayout ? '</div>' : '', "\n      ").concat(settings.fullWidthLayout ? '</div>' : '', "\n    </div>\n  ");

  // Return HTML to the page.
  return new Handlebars.SafeString(tabWrapper);
}
tabNavigation.functionName = 'tabNavigation';
export { tabNavigation };